import StatusBadge, { StatusBadgeVariants } from '../components/StatusBadge';
import moment from 'moment';
import { Icon, Icons, Info, showAlert, Table, Tooltip, TwoLinesCell, EntityChip } from '@silverfort/sf-components';
import { PRIVILEGED_ACCOUNTS_STATUS } from '@silverfort-nodelibs/common/utils/consts';
import { TIER_TO_NUMBER, TIERS } from '@silverfort-nodelibs/common/utils/consts/pam-consts';
import { PROTOCOL_DISPLAY_MAPPER } from '@silverfort-nodelibs/common/utils/consts';
import { TableStringListColumn } from '../../../sharedComponents/TableStringListColumn';

const TabsIconMap = {
    0: Icons.ShieldTier0,
    1: Icons.ShieldTier1,
    2: Icons.ShieldTier2,
    unassigned: Icons.ShieldRemove
};

const buildStatusIcon = (status, error) => {
    if (error) {
        return (
            <div>
                <Tooltip
                    title={`Multiple failed attempts to ${
                        status.code === PRIVILEGED_ACCOUNTS_STATUS.Enabling || PRIVILEGED_ACCOUNTS_STATUS.Disabled
                            ? 'enable'
                            : 'disable'
                    } the account. Refer to the audit log for more information.`}>
                    <Icon size={16} icon={Icons.Warning} className="text-sf-negative-500" />
                </Tooltip>
            </div>
        );
    }
    if (status.code === PRIVILEGED_ACCOUNTS_STATUS.Enabling || status.code === PRIVILEGED_ACCOUNTS_STATUS.Disabling) {
        return (
            <span className="animate-spin">
                <Icon size={16} icon={Icons.Loader} className="text-sf-gray-500" />
            </span>
        );
    }

    return status.time ? <Icon size={16} icon={Icons.Clock} className="text-sf-neutral-500" /> : <span />;
};

const buildExpandedTableData = (authData) => {
    const tableData = [];
    for (const authLog of authData) {
        tableData.push({
            source: authLog.source,
            destination: { title: authLog.destination, tier: authLog.tier },
            crossTier: authLog.crossTier,
            count: authLog.count,
            firstSeen: authLog.firstSeen,
            lastSeen: authLog.lastSeen,
            protocols: authLog.protocols,
            services: authLog.services,
            tierServicesIds: authLog.tierServicesIds
        });
    }

    return tableData;
};

const buildTabName = (tabName, tabId, tierDesc = '(0)') => {
    return (
        <div className="flex items-center gap-1">
            <Icon icon={TabsIconMap[tabId]} className="h-4 w-4" /> {tabName} {tierDesc && tierDesc}
        </div>
    );
};

export const buildTabs = (stats) => {
    return [
        {
            name: buildTabName('Tier 0', 0, `(${stats.tier_0 || 0})`),
            value: 0
        },
        {
            name: buildTabName('Tier 1', 1, `(${stats.tier_1 || 0})`),
            value: 1
        },
        {
            name: buildTabName(
                <div className="flex items-center gap-1 w-[70px]">
                    <Tooltip className="flex items-center gap-1" title="Coming soon">
                        <span>Tier 2</span>
                        <Info className="h-[12px] w-[12px]" />
                    </Tooltip>
                </div>,
                2,
                ''
            ),
            value: 2
        },
        {
            name: buildTabName('Unassigned', 'unassigned', `(${(stats.tier_unassigned || 0) + (stats.tier_2 || 0)})`),
            value: 'unassigned'
        }
    ];
};

export const buildFiltersQuery = (selectedFilters) => {
    const filtersArray = [];
    for (const key in selectedFilters) {
        filtersArray.push([key, selectedFilters[key]]);
    }
    return JSON.stringify(filtersArray);
};

export const buildTableColumns = (selectedTab, count = 0, onNameClick, investigationHandler) => {
    const sharedColumns = [
        {
            columnKey: 'name',
            title: `Name (${count})`,
            render: ({ name, domain, upn, guid, is_silverfort_account }) => {
                const displayName = upn || name || '';
                const truncatedName = displayName.length > 24 ? displayName.substr(0, 24) + '...' : displayName;
                return (
                    <span className="flex group">
                        <TwoLinesCell
                            topText={
                                <Tooltip title={displayName.length > 24 ? displayName : undefined}>
                                    <span
                                        className="cursor-pointer"
                                        onClick={() => onNameClick?.(guid, is_silverfort_account)}>
                                        {truncatedName}
                                    </span>
                                </Tooltip>
                            }
                            bottomText={domain || ''}
                            rightIcon={() => (
                                <div>
                                    <Tooltip title="Investigate">
                                        <div
                                            className={'cursor-pointer invisible group-hover:visible'}
                                            onClick={() => investigationHandler(guid, name)}
                                            icon={Icons.Investigate}>
                                            <Icon size={16} icon={Icons.Investigate} className="text-sf-gray-500" />
                                        </div>
                                    </Tooltip>
                                </div>
                            )}
                        />
                    </span>
                );
            }
        },
        {
            columnKey: 'status',
            title: 'Status',
            render: ({ status = {}, actionError }) => {
                return (
                    <span
                        className={`w-[10px] flex ${
                            status.code === PRIVILEGED_ACCOUNTS_STATUS.Disabled ? 'text-13 text-sf-gray-500' : ''
                        }`}>
                        <TwoLinesCell
                            topText={`${status.title}${status.code > 1 ? '...' : ''}`}
                            bottomText={status.code === PRIVILEGED_ACCOUNTS_STATUS.Enabled ? status.time : ''}
                            rightIcon={() => buildStatusIcon(status, actionError)}
                        />
                    </span>
                );
            }
        }
    ];

    const protectionColumn = {
        columnKey: 'protection',
        title: 'Policy',
        render: ({ protection = {} }) =>
            protection.jit || protection.fencing ? (
                <span className="flex gap-1">
                    {protection.jit && <StatusBadge content="JIT" variant={StatusBadgeVariants.Positive} />}
                    {protection.fencing && (
                        <StatusBadge content="Virtual Fencing" variant={StatusBadgeVariants.Positive} />
                    )}
                </span>
            ) : (
                <StatusBadge content="Unprotected" variant={StatusBadgeVariants.Negative} />
            )
    };

    const tierColumns = [
        { columnKey: 'sources', title: 'Sources' },
        { columnKey: 'destinations', title: 'Destinations' },
        {
            columnKey: 'crossTier',
            title: 'Cross tier',
            render: ({ crossTier }) => {
                return crossTier ? (
                    <span className="flex gap-2 items-center">
                        <Icon icon={Icons.ShieldBroken} size={16} className="text-sf-negative-500" /> {crossTier}
                    </span>
                ) : (
                    <span className="text-13 text-sf-gray-500">None</span>
                );
            }
        }
    ];

    if (selectedTab === 'unassigned') {
        return [...sharedColumns, protectionColumn];
    } else {
        return [...sharedColumns, ...tierColumns, protectionColumn];
    }
};

export const buildExpandedTableColumns = () => {
    return [
        { columnKey: 'source', title: 'Source' },
        {
            columnKey: 'destination',
            title: 'Destination',
            render: ({ destination }) => {
                let tier = 'Tier ' + destination?.tier;
                if (destination?.tier === null) {
                    tier = 'N/A';
                }
                return <TwoLinesCell topText={destination?.title} bottomText={tier} />;
            }
        },
        {
            columnKey: 'crossTier',
            title: 'Cross tier',
            render: ({ crossTier }) => {
                return crossTier ? (
                    <span className="flex gap-2">
                        <Icon icon={Icons.ShieldBroken} className="text-sf-negative-500" /> Yes
                    </span>
                ) : (
                    <span className="text-13 text-sf-gray-500">No</span>
                );
            }
        },
        { columnKey: 'count', title: 'Authentications' },
        {
            columnKey: 'firstSeen',
            title: 'First Seen',
            render: ({ firstSeen }) => moment(firstSeen).format('MMM DD, YYYY HH:mm:ss')
        },
        {
            columnKey: 'lastSeen',
            title: 'Last Seen',
            render: ({ lastSeen }) => moment(lastSeen).format('MMM DD, YYYY HH:mm:ss')
        },
        {
            columnKey: 'protocols',
            disableSort: true,
            title: 'Protocols',
            render: ({ protocols }) => {
                const protocolNames = protocols.map((protocol) => PROTOCOL_DISPLAY_MAPPER[protocol] || protocol);
                return <TableStringListColumn list={protocolNames} />;
            }
        },
        {
            columnKey: 'services',
            disableSort: true,
            title: 'Services',
            render: ({ services, tierServicesIds }) => {
                const maxVisible = 1;
                const list = Array.isArray(services) ? services : [];
                const visible = list.slice(0, maxVisible);
                const extraCount = Math.max(0, list.length - maxVisible);
                const tierServicesIdsSet = new Set(tierServicesIds || []);

                const chips = visible.map((service) => (
                    <EntityChip
                        className="h-8"
                        key={service.id}
                        text={service.name}
                        icon={tierServicesIdsSet.has(service.id) ? Icons.Shield : null}
                    />
                ));

                if (extraCount > 0) {
                    const remainingServices = list.slice(maxVisible);
                    chips.push(
                        <div>
                            <Tooltip
                                key="extra"
                                title={
                                    <div className="flex flex-col hover:pointer-events-none cursor-default">
                                        {remainingServices.map((s, i) => (
                                            <div key={i}>
                                                {s.name}
                                                {tierServicesIdsSet.has(s.id) ? ' (Privileged)' : null}
                                            </div>
                                        ))}
                                    </div>
                                }>
                                <EntityChip className="h-8" text={`+${extraCount}`} />
                            </Tooltip>
                        </div>
                    );
                }

                return <div className="flex items-center gap-1 whitespace-nowrap w-full">{chips}</div>;
            }
        }
    ];
};

export const buildTableData = (
    privilegedAccounts,
    accountsAuthentications,
    expandedTableColumns,
    showAuthLoader,
    authSortSettings,
    setAuthSortSettings,
    setAuthChangeTarget,
    crossTierOnly
) => {
    const tableData = [];

    if (!privilegedAccounts?.length) {
        return tableData;
    }

    for (const user of privilegedAccounts) {
        const data = {
            name: user.name,
            guid: user.guid,
            upn: user.upn,
            domain: user.domain,
            tier: user.tier === TIER_TO_NUMBER[TIERS.TWO] ? null : user.tier,
            actionError: user.actionError,
            status: {
                title: Object.keys(PRIVILEGED_ACCOUNTS_STATUS).find(
                    (key) => PRIVILEGED_ACCOUNTS_STATUS[key] === user.status
                ),
                time: user.enabledTimeLeft,
                code: user.status
            },
            sources: user.sources,
            destinations: user.destinations,
            crossTier: user.crossTier,
            protection: { jit: user.jitEnabled, fencing: user.fencingEnabled },
            is_silverfort_account: user.is_silverfort_account
        };

        const authentications = accountsAuthentications[user.guid];

        const tableHeight = accountsAuthentications[user.guid]?.length * 48 || 48;

        if (data.tier !== null && (data.sources || data.destinations)) {
            const isLoading = showAuthLoader[user.guid];
            const expandedRows = buildExpandedTableData(
                (crossTierOnly ? authentications?.filter((auth) => auth.crossTier) : authentications) || []
            );

            data.expandedTable = (
                <Table
                    disableHighlightedFirstColumn={true}
                    bodyHeight={Math.min(tableHeight, 300)}
                    tableContainerClassName={'w-full h-fit !border-sf-gray-100'}
                    rows={!isLoading ? expandedRows : []}
                    columns={expandedTableColumns}
                    loading={isLoading}
                    defaultSortColumn={authSortSettings[user.guid]?.orderBy || 'count'}
                    defaultSortDirection={authSortSettings[user.guid]?.sortDirection || 'desc'}
                    onSortChange={(orderBy, sortDirection) => {
                        setAuthChangeTarget(user.guid);
                        setAuthSortSettings({ ...authSortSettings, [user.guid]: { orderBy, sortDirection } });
                    }}
                />
            );
        }

        tableData.push(data);
    }

    return tableData;
};

export const buildPolicyAlertPopup = (account, jitEnabled, fencingEnabled) => {
    // JIT Popup
    const showJitEnabledPopup = !account.jitEnabled && jitEnabled;
    const showJitDisabledPopup = account.jitEnabled && !jitEnabled;

    if (showJitEnabledPopup || showJitDisabledPopup) {
        showAlert('', 'success', {
            title: `JIT Policy ${showJitEnabledPopup ? 'enabled' : 'disabled'} for ${account.name}`,
            icon: Icons.CircleCheck
        });
    }

    // Fencing Popup
    const showFencingEnabledPopup = !account.fencingEnabled && fencingEnabled;
    const showFencingDisabledPopup = account.fencingEnabled && !fencingEnabled;

    if (showFencingEnabledPopup || showFencingDisabledPopup) {
        showAlert(
            `Virtual Fencing Policy ${showFencingEnabledPopup ? 'enabled' : 'disabled'} for ${account.name}`,
            'success',
            {
                title: 'Success',
                icon: Icons.CircleCheck
            }
        );
    }
};
